import React, { useState, useEffect, useRef } from "react";
import LottieIcon from "../common/LottieIcon";
import { getIconCategoryClass, getIconCategoryColor } from "../common/iconCategory";
import EditablePreview from "../v2/CustomisationWorkspace/WorkspaceComponents/EditablePreview/EditablePreview";
import { ReactComponent as PencilIcon } from '../../assets/icons/Pencil.svg';
import { hasTriggerCommandParam, CommandParamField } from "../v2/CustomisationWorkspace/WorkspaceComponents/ModuleList/customFields/disruptor/DisruptorField";

const FlowPanel = React.memo(({
  item,
  stepIndex,
  dynamicStyle,
  hoveredStackingItem,
  hoveredTimelineComponentId,
  stackingOrder,
  expandedComponents,
  dragOverStep,
  isScrollingRef,
  isScrolling,
  isPanning,
  onPanelDragStart,
  onPanelDragEnd,
  onStepNameChange,
  onComponentNameChange,
  isEditingName,
  setIsEditingName,
  onComponentDragStart,
  onComponentDragEnd,
  onStepContextMenu,
  onComponentContextMenu,
  onInsertionDrop,
  onFlowAreaDragOver,
  onComponentClick,
  onComponentHover,
  onEditComponentCanvas,
  campaignData,
  customisationVars,
  leftListNewCustomisationItems,
  selectedFeatureId,
  selectedFeatureNode,
  selectedItem,
  onComponentTimingChange,
  panOffset,
  getFlowStepListNodesForPreviewFunc,
  activateStepFunc,
  previewRefreshToken,
}) => {
  const [updateForcer, setUpdateForcer] = useState(0);
  const forceUpdate = () => setUpdateForcer(prev => prev + 1);

  const normalizeId = (id) => (id === undefined || id === null ? null : String(id));
  const hoveredId = normalizeId(hoveredStackingItem);
  const hoveredTimelineId = normalizeId(hoveredTimelineComponentId);

  // Local state for step name editing
  const [editingStepName, setEditingStepName] = useState(item.label);
  const [componentNameDrafts, setComponentNameDrafts] = useState({});
  const inputRef = useRef(null);
  
  // Update local state when item.label changes from parent
  useEffect(() => {
    setEditingStepName(item.label);
  }, [item.label]);

  useEffect(() => {
    setComponentNameDrafts(prevDrafts => {
      const nextDrafts = {};

      (item.components || []).forEach((component) => {
        nextDrafts[component.id] = component.label;
      });

      return nextDrafts;
    });
  }, [item.components]);
  
  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditingName === item.id && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingName, item.id]);
  
  const handlePanelDragStart = (e) => {
    // Don't allow drag if step is locked
    if (item.locked) {
      e.preventDefault();
      return;
    }
    
    // Don't allow drag if editing a name
    if (isEditingName) {
      e.preventDefault();
      return;
    }
    
    // Only allow panel drag if dragging from safe areas (not from component items)
    // Check if the drag started directly from a component item
    const componentItem = e.target.closest('.component-item');
    
    // If we're dragging from a component item, prevent panel drag
    if (componentItem) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Allow drag from panel header, panel-image-placeholder, or panel background
    onPanelDragStart(item);
  };

  let previewEntireStep = true;
  const findComponentToPreview = () => {
    /* console.log('Finding component to preview for panel', item, {
      hoveredStackingItem,
      hoveredTimelineComponentId,
      selectedFeatureId
    }); */
    let foundComponent = item.components[0];
    if (hoveredTimelineComponentId) {
      const hoveredTimelineComponentIdNormalized = normalizeId(hoveredTimelineComponentId);
      const matchingComponent = item.components.find(c => normalizeId(c.featureId || c.campaignFeatureId) === hoveredTimelineComponentIdNormalized);
      if (matchingComponent) {
        foundComponent = matchingComponent;
        previewEntireStep = false;
      }
    } else if (hoveredStackingItem) {
      const hoveredStackingItemNormalized = normalizeId(hoveredStackingItem);
      const matchingComponent = item.components.find(c => normalizeId(c.featureId || c.campaignFeatureId) === hoveredStackingItemNormalized);
      if (matchingComponent) {
        foundComponent = matchingComponent;
        previewEntireStep = false;
      }
    } else if (selectedFeatureId) {
      const selectedFeatureIdNormalized = normalizeId(selectedFeatureId);
      const matchingComponent = item.components.find(c => normalizeId(c.featureId || c.campaignFeatureId) === selectedFeatureIdNormalized);
      if (matchingComponent) {
        foundComponent = matchingComponent;
        previewEntireStep = false;
      }
    }
    return foundComponent;
  };
  const componentToPreview = item.components ? findComponentToPreview() : null;
  const componentToPreviewId = normalizeId(componentToPreview?.featureId ?? componentToPreview?.campaignFeatureId);

  // Need to detect when the itemPreview div moves offscreen and hide / show the contents based on a state variable. We have a panOffset prop that has x,y values, we can use that to detect when the canvas is being moved.
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      const itemPreviewElement = window.document.getElementById(`itemPreview_${item.id}`);
      if (itemPreviewElement) {
        const rect = itemPreviewElement.getBoundingClientRect();
        // console.log('Checking item preview visibility with panOffset', panOffset, 'rect:', rect);
        const isOffscreen = rect.right < 0 || rect.left > window.innerWidth || rect.bottom < 0 || rect.top > window.innerHeight;
        setIsPreviewVisible(!isOffscreen);
      }
    }, 250); // Check every 250ms

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  console.log('Rendering FlowPanel for item', item, 'with preview visibility:', isPreviewVisible, 'and componentToPreviewId:', componentToPreviewId);

  const commandParams = {};
  // Step through the item components, if we have a commandParam store it in our commandParams object with the featureId as the key
  item.components?.forEach((component) => {
    if (hasTriggerCommandParam(component.featureId || component.campaignFeatureId, campaignData)) {
      commandParams[normalizeId(component.featureId || component.campaignFeatureId)] = component.commandParam;
    }
  });
  console.log('FlowPanel commandParams for item', item, commandParams);

  return (
    <div
      className={`flow-panel ${item.locked ? 'locked' : ''}`}
      data-panel-id={item.id}
      data-step-id={item.stepId || item.id}
      style={{
        ...dynamicStyle,
        ...((isPanning || isScrolling) ? { pointerEvents: 'none' } : {}),
        ...(item.locked && {
          border: '2px dashed #999',
          backgroundColor: 'rgba(200, 200, 200, 0.1)',
          boxShadow: 'none',
          cursor: 'not-allowed',
          opacity: 0.6,
          filter: 'grayscale(0.5)',
          transition: 'opacity 0.2s ease, filter 0.2s ease',
        })
      }}
      draggable={!item.locked && !isScrolling && !isPanning}
      onDragStart={handlePanelDragStart}
      onDragEnd={onPanelDragEnd}
      onDragOver={(e) => {
        // Allow dropping components into this step
        if (!item.locked) {
          e.preventDefault();
        }
        // Let event bubble naturally to canvas's onDragOver handler
      }}
      onDrop={(e) => {
        // Handle dropping components into this step
        if (!item.locked && onInsertionDrop) {
          e.preventDefault();
          e.stopPropagation();
          onInsertionDrop(e);
        }
      }}
      onMouseDown={(e) => {
        // Stop mousedown from bubbling to canvas to prevent panning when clicking panel/components
        e.stopPropagation();
      }}
      onMouseLeave={() => {
        if (onComponentHover) onComponentHover(null);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onStepContextMenu) {
          onStepContextMenu(e, item);
        }
      }}
    >
      <div className="panel-header">
        {item.locked && (
          <div style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            background: '#666',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            zIndex: 10,
            border: '1.5px solid white'
          }}>
            🔒
          </div>
        )}
        <span className="panel-step-badge">{item.sequenceLabel || '--'}</span>
        <input 
          ref={inputRef}
          type="text" 
          className="panel-title-input" 
          value={editingStepName} 
          onChange={(e) => setEditingStepName(e.target.value)}
          onClick={() => !item.locked && setIsEditingName(item.id)}
          onBlur={(e) => {
            if (!item.locked) {
              onStepNameChange(item.stepId || item.id, e.target.value, true);
            }
            setIsEditingName(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            }
            if (e.key === 'Escape') {
              setEditingStepName(item.label);
              setIsEditingName(null);
            }
          }}
          onDragStart={(e) => e.preventDefault()}
          draggable={false}
          disabled={item.locked}
          style={item.locked ? { cursor: 'not-allowed', backgroundColor: '#f5f5f5', color: '#999' } : {}}
        />
      </div>
      {/* <div className="panel-image-placeholder"></div> */}
      {<div className="item-preview" id={`itemPreview_${item.id}`}>
        {isPreviewVisible && item.components &&
          <>
            <EditablePreview
                campaignData={campaignData}
                customisationVars={customisationVars}
                selectedFeatureId={componentToPreviewId}
                selectedFeatureNode={leftListNewCustomisationItems?.[componentToPreviewId] ?? null}
                activeNode={leftListNewCustomisationItems?.[componentToPreviewId] ?? null}
                focusEditingOnNode={() => {}}
                setFocusEditingOnNode={() => {}}
                visualisedNode={leftListNewCustomisationItems?.[componentToPreviewId] ?? null}
                timelinePlayheadStep={0}
                timelinePlayheadLabel={'Start'}
                activateNodeFunction={() => {}}
                forceUpdateComponent={() => {}}
                forceUpdate={() => {}}
                forceUpdateModuleListFunc={() => {}}
                timelineHeight={0}

                overridePreviewWindowId={'itemPreviewWindow_' + componentToPreviewId + '_' + item.id}
                overrideContainerId={'itemPreviewContainer_' + componentToPreviewId + '_' + item.id}

                thumbnailMode={true}

                layerNodes={previewEntireStep ? getFlowStepListNodesForPreviewFunc?.(stepIndex) : null}
                onComponentHover={onComponentHover}

                commandParams={commandParams}
            />
            {(!leftListNewCustomisationItems?.[componentToPreviewId] || leftListNewCustomisationItems?.[componentToPreviewId].isLegacy) &&
              <>
                <div className="legacy-preview-overlay">
                  <h5>Legacy Component</h5>
                </div>
              </>
            }
          </>
        }
      </div>}
      <div className="panel-content">
        {item.components ? (
          <div 
            className="components-list"
            onDragOver={(e) => {
              // Allow dropping - let event bubble to parent and canvas
              if (!item.locked) {
                e.preventDefault();
              }
            }}
            onDrop={(e) => {
              if (!item.locked) {
                e.preventDefault();
                e.stopPropagation();
                onInsertionDrop && onInsertionDrop(e);
              }
            }}
          >
            {item.components
              .slice()
              .sort((a, b) => {
                // Sort components by their stacking order (zIndex)
                // Find zIndex from stackingOrder prop
                const aStackItem = stackingOrder?.find(s => s.featureId === (a.featureId || a.campaignFeatureId));
                const bStackItem = stackingOrder?.find(s => s.featureId === (b.featureId || b.campaignFeatureId));
                const aIndex = aStackItem?.zIndex ?? 999;
                const bIndex = bStackItem?.zIndex ?? 999;
                return aIndex - bIndex;
              })
              .map((component, index) => {
              const isExpanded = Boolean(component.isExpansionLinked);
              const isExpandedCopy = component.isExpandedCopy || false;
              const rawCategory = component.iconCategory || component.featureCategory;
              const expansionColor = getIconCategoryColor(rawCategory);
              const componentFeatureId = normalizeId(component.featureId || component.campaignFeatureId);
              const componentIdNormalized = normalizeId(component.id);
              const isComponentHighlighted = !isScrollingRef?.current && hoveredId && (hoveredId === componentFeatureId);
              const isTimelineHighlighted = hoveredTimelineId && componentIdNormalized && (hoveredTimelineId === componentIdNormalized);
              const isHighlighted = isComponentHighlighted || isTimelineHighlighted;
              const selectedFeatureIdNormalized = normalizeId(selectedFeatureId);
              const isNodeSelected = Boolean(selectedFeatureIdNormalized) && selectedFeatureIdNormalized === componentFeatureId;
              const selectedItemIdNormalized = normalizeId(selectedItem?.id);
              const selectedStepIdNormalized = normalizeId(selectedItem?.selectedStepId || selectedItem?.homeStepId);
              const currentStepIdNormalized = normalizeId(item.stepId || item.id);
              const isSelectedComponent = Boolean(selectedItemIdNormalized)
                && selectedItemIdNormalized === componentIdNormalized
                && (!selectedStepIdNormalized || selectedStepIdNormalized === currentStepIdNormalized);
              const showExtraSettingsMenu = isSelectedComponent && !isExpandedCopy;
              const componentAutoStart = component.autoStart !== false;
              const componentStartDelay = Number.isFinite(Number(component.startDelay)) ? Number(component.startDelay) : 0;
              const componentStartDelayMinutes = Math.floor(componentStartDelay / 60);
              const componentStartDelaySeconds = componentStartDelay % 60;
              const setStartDelayMinutes = (minutes) => {
                const nextMinutes = Math.max(0, Math.min(29, Number(minutes) || 0));
                onComponentTimingChange?.(component.id, { startDelay: (nextMinutes * 60) + componentStartDelaySeconds });
              };
              const setStartDelaySeconds = (seconds) => {
                const nextSeconds = Math.max(0, Math.min(59, Number(seconds) || 0));
                onComponentTimingChange?.(component.id, { startDelay: (componentStartDelayMinutes * 60) + nextSeconds });
              };

              
              return (
                <div 
                  key={`${component.id}-${index}`}
                  data-component-id={component.id}
                  className={`component-item ${isExpanded ? 'expansion-linked' : ''} ${isExpandedCopy ? 'expanded-copy' : ''} ${isHighlighted ? 'stacking-highlighted' : ''} ${isNodeSelected && !isHighlighted ? 'node-selected' : ''}`}
                  style={{
                    '--item-category-color': expansionColor,
                    ...(isExpanded ? { '--expansion-link-color': expansionColor } : {})
                  }}
                  draggable={!isExpandedCopy && !isEditingName && !item.locked}
                  onMouseEnter={() => {
                    if (onComponentHover) {
                      const featureId = component.featureId || component.campaignFeatureId;
                      onComponentHover(featureId ?? null);
                    }
                  }}
                  onMouseLeave={() => {
                    if (onComponentHover) {
                      onComponentHover(null);
                    }
                  }}
                  onClick={(e) => {
                    if (!isEditingName && !item.locked && onComponentClick) {
                      e.stopPropagation();
                      onComponentClick(component, {
                        clickedStepId: item.stepId || item.id,
                        source: 'flow-panel',
                      });
                    }
                  }}
                  onDragStart={(e) => {
                    // Don't allow drag if step is locked
                    if (item.locked) {
                      e.preventDefault();
                      return;
                    }
                    
                    // Don't allow drag if editing a name
                    if (isEditingName) {
                      e.preventDefault();
                      return;
                    }
                    
                    if (isExpandedCopy) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    
                    // Set drag data for HTML5 drag and drop
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', component.id);
                    
                    e.stopPropagation();
                    onComponentDragStart && onComponentDragStart(component, item.stepId || item.id);
                  }}
                  onDragOver={(e) => {
                    // Prevent default to allow drop on other components/steps
                    // Don't stopPropagation - let event bubble to set dragOverStep
                    e.preventDefault();
                  }}
                  onDragEnd={(e) => {
                    if (isExpandedCopy) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    onComponentDragEnd && onComponentDragEnd();
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onComponentContextMenu) {
                      onComponentContextMenu(e, component, item);
                    }
                  }}
                >
                  <div className="component-main-row">
                    <span className={`component-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(component.iconCategory) ? component.iconCategory[0] : component.iconCategory)}`}><LottieIcon icon={component.icon} varKey={component.varKey} size={20} /></span>
                    {component.isPrimaryStepItem && !isExpandedCopy && (
                      <span
                        title="Key Feature in Chain"
                        style={{
                          color: '#7ed957',
                          fontSize: '12px',
                          lineHeight: 1,
                          marginRight: '0rem',
                          flexShrink: 0,
                        }}
                      >
                        ★
                      </span>
                    )}
                    {!isExpandedCopy ? (
                      <input 
                        type="text" 
                        className="component-label-input" 
                        value={componentNameDrafts[component.id] ?? component.label}
                        onChange={(e) => {
                          if (!item.locked) {
                            e.stopPropagation();
                            setComponentNameDrafts(prevDrafts => ({
                              ...prevDrafts,
                              [component.id]: e.target.value
                            }));
                            onComponentNameChange && onComponentNameChange(component.id, e.target.value, false);
                          }
                        }}
                        onBlur={(e) => {
                          if (!item.locked) {
                            e.stopPropagation();
                            setComponentNameDrafts(prevDrafts => ({
                              ...prevDrafts,
                              [component.id]: e.target.value
                            }));
                            onComponentNameChange && onComponentNameChange(component.id, e.target.value, true);
                          }
                          setIsEditingName(null);
                        }}
                        onFocus={(e) => {
                          if (!item.locked) {
                            e.stopPropagation();
                            setIsEditingName(`component-${component.id}`);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!item.locked && onComponentClick) {
                            onComponentClick(component, {
                              clickedStepId: item.stepId || item.id,
                              source: 'flow-panel-input',
                            });
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();

                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }

                          if (e.key === 'Escape') {
                            setComponentNameDrafts(prevDrafts => ({
                              ...prevDrafts,
                              [component.id]: component.label
                            }));
                            setIsEditingName(null);
                            e.currentTarget.blur();
                          }
                        }}
                        onDragStart={(e) => e.preventDefault()}
                        draggable={false}
                        disabled={item.locked}
                        style={item.locked ? { cursor: 'not-allowed', backgroundColor: '#f5f5f5', color: '#999', opacity: 0.7 } : {}}
                      />
                    ) : (
                      <span className="component-label">
                        {component.label}
                        <span className="expanded-indicator"> (expanded)</span>
                      </span>
                    )}
                    {!isExpandedCopy && !item.locked && onEditComponentCanvas && (
                      <span
                        className="component-edit-btn"
                        title="Open in Canvas"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onEditComponentCanvas(component, {
                            clickedStepId: item.stepId || item.id,
                            source: 'flow-panel-open',
                          });
                          activateStepFunc?.(item.stepId || item.id);
                        }}
                      >
                        <PencilIcon aria-label="Open in Canvas" />
                      </span>
                    )}
                  </div>
                  {showExtraSettingsMenu && hasTriggerCommandParam(component.featureId || component.campaignFeatureId, campaignData) &&
                    <div className={`component-timing-menu v2-module-columns-holder`}>
                      <CommandParamField
                        disruptorId={component.featureId || component.campaignFeatureId}
                        disruptorParamData={component.commandParam}
                        customisationVars={customisationVars.current[component.featureId || component.campaignFeatureId]}
                        campaignData={campaignData}
                        isEnabled={true}
                        hideNotes={true}
                        onChange={
                          (fieldId, value) => {
                            console.log(`DisruptorField: onChange for featureId ${component.featureId || component.campaignFeatureId}, fieldId: ${fieldId}, value:`, value);
                            component.commandParam = value;
                            onComponentTimingChange?.(component.id, { commandParam: value });
                            forceUpdate(); // Force re-render to reflect updated commandParam
                          }
                        }
                      />
                    </div>
                  }
                  {showExtraSettingsMenu && (
                    <div className="component-timing-menu" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                      <div className="component-timing-heading">Timing</div>
                      <label className="component-timing-toggle">
                        <input
                          type="checkbox"
                          checked={componentAutoStart}
                          onChange={(e) => onComponentTimingChange?.(component.id, { autoStart: e.target.checked })}
                        />
                        <span className="component-timing-toggle-label">Auto Start</span>
                      </label>
                      <div className={`component-delay-control${componentAutoStart ? '' : ' is-disabled'}`}>
                        <div className="component-delay-label">Start Delay</div>
                        <div className="component-delay-grid">
                          <label className="component-delay-field">
                            <span className="component-delay-field-label">Min</span>
                            <div className="component-delay-input-wrap">
                              <input
                                type="number"
                                min="0"
                                max="29"
                                step="1"
                                value={componentStartDelayMinutes}
                                disabled={!componentAutoStart}
                                onChange={(e) => setStartDelayMinutes(e.target.value)}
                              />
                              <div className="component-delay-stepper" aria-hidden={!componentAutoStart}>
                                <button
                                  type="button"
                                  className="component-delay-stepper-button component-delay-stepper-button--up"
                                  disabled={!componentAutoStart || componentStartDelayMinutes >= 29}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartDelayMinutes(componentStartDelayMinutes + 1);
                                  }}
                                >
                                  <span>▲</span>
                                </button>
                                <button
                                  type="button"
                                  className="component-delay-stepper-button component-delay-stepper-button--down"
                                  disabled={!componentAutoStart || componentStartDelayMinutes <= 0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartDelayMinutes(componentStartDelayMinutes - 1);
                                  }}
                                >
                                  <span>▼</span>
                                </button>
                              </div>
                            </div>
                          </label>
                          <label className="component-delay-field">
                            <span className="component-delay-field-label">Sec</span>
                            <div className="component-delay-input-wrap">
                              <input
                                type="number"
                                min="0"
                                max="59"
                                step="1"
                                value={componentStartDelaySeconds}
                                disabled={!componentAutoStart}
                                onChange={(e) => setStartDelaySeconds(e.target.value)}
                              />
                              <div className="component-delay-stepper" aria-hidden={!componentAutoStart}>
                                <button
                                  type="button"
                                  className="component-delay-stepper-button component-delay-stepper-button--up"
                                  disabled={!componentAutoStart || componentStartDelaySeconds >= 59}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartDelaySeconds(componentStartDelaySeconds + 1);
                                  }}
                                >
                                  <span>▲</span>
                                </button>
                                <button
                                  type="button"
                                  className="component-delay-stepper-button component-delay-stepper-button--down"
                                  disabled={!componentAutoStart || componentStartDelaySeconds <= 0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStartDelaySeconds(componentStartDelaySeconds - 1);
                                  }}
                                >
                                  <span>▼</span>
                                </button>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {dragOverStep && dragOverStep.id === item.id && (
              <div className="component-drop-zone">Drop component here</div>
            )}
          </div>
        ) : item.dummyData ? (
          <div className="panel-data">
            {Object.entries(item.dummyData).map(([key, value], index) => (
              <div key={key} className="data-row">
                <span className="data-label">{key}:</span>
                <span className="data-value">
                  {Array.isArray(value) ? value.join(', ') : String(value)}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders during drag operations
  // Only re-render if meaningful props have changed
  const prevIsDragOver = prevProps.dragOverStep?.id === prevProps.item.id;
  const nextIsDragOver = nextProps.dragOverStep?.id === nextProps.item.id;
  
  // Check if THIS panel's components are affected by hover state
  const normalizeId = (id) => (id === undefined || id === null ? null : String(id));
  const prevHovered = normalizeId(prevProps.hoveredStackingItem);
  const nextHovered = normalizeId(nextProps.hoveredStackingItem);
  const prevTimelineHovered = normalizeId(prevProps.hoveredTimelineComponentId);
  const nextTimelineHovered = normalizeId(nextProps.hoveredTimelineComponentId);
  const prevSelectedItemId = normalizeId(prevProps.selectedItem?.id);
  const nextSelectedItemId = normalizeId(nextProps.selectedItem?.id);
  const prevSelectedStepId = normalizeId(prevProps.selectedItem?.selectedStepId || prevProps.selectedItem?.homeStepId);
  const nextSelectedStepId = normalizeId(nextProps.selectedItem?.selectedStepId || nextProps.selectedItem?.homeStepId);
  const prevPanelStepId = normalizeId(prevProps.item.stepId || prevProps.item.id);
  const nextPanelStepId = normalizeId(nextProps.item.stepId || nextProps.item.id);
  const prevHasHoveredComponent = prevProps.item.components?.some(c => 
    normalizeId(c.featureId || c.campaignFeatureId) === prevHovered
  );
  const nextHasHoveredComponent = nextProps.item.components?.some(c => 
    normalizeId(c.featureId || c.campaignFeatureId) === nextHovered
  );
  const prevHasTimelineComponent = prevProps.item.components?.some(c => 
    normalizeId(c.id) === prevTimelineHovered
  );
  const nextHasTimelineComponent = nextProps.item.components?.some(c => 
    normalizeId(c.id) === nextTimelineHovered
  );
  const prevHasSelectedComponent = prevProps.item.components?.some(c => 
    normalizeId(c.id) === prevSelectedItemId && (!prevSelectedStepId || prevSelectedStepId === prevPanelStepId)
  );
  const nextHasSelectedComponent = nextProps.item.components?.some(c => 
    normalizeId(c.id) === nextSelectedItemId && (!nextSelectedStepId || nextSelectedStepId === nextPanelStepId)
  );
  
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.label === nextProps.item.label &&
    prevProps.item.locked === nextProps.item.locked &&
    prevProps.item.x === nextProps.item.x &&
    prevProps.item.y === nextProps.item.y &&
    prevProps.isEditingName === nextProps.isEditingName &&
    prevIsDragOver === nextIsDragOver &&
    prevHovered === nextHovered &&
    prevHasHoveredComponent === nextHasHoveredComponent &&
    normalizeId(prevProps.selectedFeatureId) === normalizeId(nextProps.selectedFeatureId) &&
    prevSelectedItemId === nextSelectedItemId &&
    prevSelectedStepId === nextSelectedStepId &&
    prevHasSelectedComponent === nextHasSelectedComponent &&
    prevHasTimelineComponent === nextHasTimelineComponent &&
    prevProps.previewRefreshToken === nextProps.previewRefreshToken &&
    JSON.stringify(prevProps.item.components?.map(c => ({ id: c.id, label: c.label, stepLength: c.stepLength, startDelay: c.startDelay, autoStart: c.autoStart, isExpandedCopy: c.isExpandedCopy, isExpansionLinked: c.isExpansionLinked }))) === 
    JSON.stringify(nextProps.item.components?.map(c => ({ id: c.id, label: c.label, stepLength: c.stepLength, startDelay: c.startDelay, autoStart: c.autoStart, isExpandedCopy: c.isExpandedCopy, isExpansionLinked: c.isExpansionLinked })))
  );
});

export default FlowPanel;
